import React, { useState } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

export default function EmployeesView({ employees, loading, logAction, isReadOnly }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // File Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Initial State with defaults for ALL fields to prevent uncontrolled input errors
  const initialFormState = { 
    name: '', role: '', email: '', phone: '', startDate: '', 
    personalId: '', status: 'Active', imageFile: '', 
    accessRole: 'Read-Only Visitor'
  };
  const [formData, setFormData] = useState(initialFormState);
  
  const openModal = (employee = null) => {
    if (employee) {
      // FIX: Merge initialFormState with the employee object.
      // This ensures that if 'employee' is missing a field (e.g. 'personalId'), 
      // it defaults to the empty string from initialFormState instead of undefined.
      setFormData({ ...initialFormState, ...employee });
      setEditingId(employee.id);
      setIsEditing(true);
    } else {
      setFormData(initialFormState);
      setIsEditing(false);
    }
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      let imageUrl = formData.imageFile;

      if (selectedFile) {
        const storageRef = ref(storage, `employee-images/${Date.now()}_${selectedFile.name}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const finalData = { ...formData, imageFile: imageUrl };
      
      // Sanitize: Remove undefined values before saving to Firestore
      Object.keys(finalData).forEach(key => finalData[key] === undefined && delete finalData[key]);

      if (isEditing) {
        await updateDoc(doc(db, "employees", editingId), finalData);
        logAction("Update Employee", `Updated ${formData.name}`);
      } else {
        await addDoc(collection(db, "employees"), finalData);
        logAction("Create Employee", `Created ${formData.name}`);
      }
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert("Error saving employee.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if(isReadOnly) return;
    if (!window.confirm(`Permanently delete ${name}?`)) return;
    try { 
        await deleteDoc(doc(db, "employees", id)); 
        logAction("Delete Employee", `Deleted ${name}`);
    } catch (err) { console.error(err); }
  };

  const filtered = employees.filter(e => 
    (e.name + e.role + e.email).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container-fluid p-0">
      
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
            <h2 className="fw-bold text-main">Employees</h2>
            <p className="text-muted-theme">Manage internal staff and contact details.</p>
        </div>
        {!isReadOnly && (
            <div className="d-flex gap-2">
                <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search..." 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                />
                <button className="btn btn-primary" onClick={() => openModal()}>
                    <i className="bi bi-person-plus-fill"></i> Add
                </button>
            </div>
        )}
      </div>

      {/* GRID */}
      {loading ? <div className="text-main">Loading...</div> : (
        <div className="row g-4">
          {filtered.map((e) => (
            <div key={e.id} className="col-md-6 col-xl-4">
              <div className={`card h-100 bg-card border-theme shadow-sm ${e.status === 'Archived' ? 'opacity-50' : ''}`}>
                <div className="card-body">
                  
                  <div className="d-flex align-items-center mb-4">
                    {e.imageFile ? (
                      <img 
                        src={e.imageFile} 
                        alt={e.name} 
                        className="rounded-circle object-fit-cover border border-theme" 
                        style={{width: 60, height: 60}} 
                      />
                    ) : (
                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold" style={{width: 60, height: 60, fontSize: 24}}>
                        {e.name.charAt(0)}
                      </div>
                    )}
                    <div className="ms-3">
                      <h5 className="card-title mb-0 text-main">{e.name}</h5>
                      <small className="text-accent">{e.role}</small>
                      <span className={`badge ms-2 ${e.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>{e.status}</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-envelope text-secondary me-2"></i>
                      <a href={`mailto:${e.email}`} className="text-muted-theme text-decoration-none">{e.email}</a>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-telephone text-secondary me-2"></i>
                      <a href={`tel:${e.phone}`} className="text-muted-theme text-decoration-none">{e.phone}</a>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-calendar3 text-secondary me-2"></i>
                      <span className="text-muted-theme small">Started: {e.startDate}</span>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4 pt-3 border-top border-theme">
                     {!isReadOnly ? (
                         <>
                            <button className="btn btn-sm btn-outline-secondary w-100" onClick={() => openModal(e)}>Edit</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(e.id, e.name)}><i className="bi bi-trash"></i></button>
                         </>
                     ) : (
                         <button className="btn btn-sm btn-outline-secondary w-100" disabled>View Only</button>
                     )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              
              <div className="modal-header">
                <h5 className="modal-title text-main">{isEditing ? "Edit Employee" : "Add Employee"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              
              <div className="modal-body">
                <form onSubmit={handleSave}>
                    <div className="row g-2 mb-3">
                        <div className="col">
                            <label className="text-muted-theme small">Name</label>
                            <input className="form-control" required value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
                        </div>
                        <div className="col">
                            <label className="text-muted-theme small">Role</label>
                            <input className="form-control" required value={formData.role || ''} onChange={e => setFormData({...formData, role: e.target.value})} />
                        </div>
                    </div>
                    <div className="row g-2 mb-3">
                        <div className="col">
                            <label className="text-muted-theme small">Email</label>
                            <input type="email" className="form-control" required value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} />
                        </div>
                        <div className="col">
                            <label className="text-muted-theme small">Phone</label>
                            <input type="tel" className="form-control" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} />
                        </div>
                    </div>
                    <div className="row g-2 mb-3">
                        <div className="col">
                            <label className="text-muted-theme small">Start Date</label>
                            <input type="date" className="form-control" value={formData.startDate || ''} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                        </div>
                        <div className="col">
                            <label className="text-muted-theme small">Personal ID</label>
                            <input className="form-control" value={formData.personalId || ''} onChange={e => setFormData({...formData, personalId: e.target.value})} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="text-muted-theme small">Status</label>
                        <select className="form-select" value={formData.status || 'Active'} onChange={e => setFormData({...formData, status: e.target.value})}>
                            <option>Active</option>
                            <option>Archived</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="text-muted-theme small">Profile Picture</label>
                        <input type="file" accept="image/*" className="form-control" onChange={handleFileChange} />
                    </div>
                    <div className="mb-3">
                        <label className="text-muted-theme small">App Access Role</label>
                        <select className="form-select" value={formData.accessRole || 'Read-Only Visitor'} onChange={e => setFormData({...formData, accessRole: e.target.value})}>
                            <option>Super Admin</option>
                            <option>Read-Only Admin</option>
                            <option>Read-Only Visitor</option>
                        </select>
                    </div>
                    
                    <div className="modal-footer border-theme w-100">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={isUploading}>
                            {isUploading ? "Uploading..." : "Save Employee"}
                        </button>
                    </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}